import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import SubscriptionApi from '../subscription/subscriptionApi';
import AccountApi from '../account/accountApi';
import { getAccountIdFromSessionStorage, formatTimestamp } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { cardStyle } from '../components/common/CreditCard';
import { SubscriptionAllDetails, SubscriptionPaymentPayload } from '../subscription/subscriptionType';
import { updateSessionStorageSubscriptionStatus } from '../lib/global_function';
import { ReactComponent as PoweredByStripe } from '../components/image/creditCard/powered_by_stripe.svg';
import BackofCard from '../components/image/creditCard/back-of-card.png';
import Visa from '../components/image/creditCard/visa.png';
import MasterCard from '../components/image/creditCard/master.png';
import Amex from '../components/image/creditCard/amx.png';
import Discover from '../components/image/creditCard/discover.png';
import RedCross from '../components/image/creditCard/wrong_card.png';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { FORMATTED_MONTHLY_FEE, MONTHLY_FEE } from '../components/common/constants/constants';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import Spinner from '../components/ui/spinner';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string);

const MakePaymentPage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Account', path: '/main/account' },
    { label: 'Subscription', path: '/main/account/subscription' },
    { label: 'Make Payment', path: '/main/account/make_payment' },
  ];

  const cardIcons: Record<string, string> = {
    visa: Visa,
    mastercard: MasterCard,
    amex: Amex,
    discover: Discover,
  };

  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionAllDetails | null>(null);
  const [numberOfMonths, setNumberOfMonths] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [cardBrand, setCardBrand] = useState('');
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [accountEmail, setAccountEmail] = useState<string | ''>('');

  useEffect(() => {
    const accountId = getAccountIdFromSessionStorage(
      process.env.REACT_APP_SESSION_STORAGE_KEY as string
    );
    if (!accountId) {
      setErrorMessage('Account ID is missing.');
      setLoading(false);
      return;
    }

    const fetchSubscriptions = async () => {
      try {
        const response = await SubscriptionApi.getSubscriptionAndAll();
        setSubscriptionData(response);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      } finally {
        setLoading(false);
      }
    };

    const fetchAccount = async () => {
      try {
        const accountResponse = await AccountApi.getAccount(accountId);
        setAccountEmail(accountResponse.email);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
    fetchAccount();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setErrorMessage('Stripe has not been initialized.');
      return;
    }

    const cardNumber = elements.getElement(CardNumberElement);
    if (!cardNumber) {
      setErrorMessage('Card input is not ready.');
      return;
    }

    const accountId = getAccountIdFromSessionStorage(
      process.env.REACT_APP_SESSION_STORAGE_KEY as string
    );
    if (!accountId) {
      setErrorMessage('Account ID is missing.');
      return;
    }

    if (numberOfMonths === null || numberOfMonths === undefined || String(numberOfMonths) === '') {
      setErrorMessage('Value of number of months is missing.');
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      });

      if (error || !paymentMethod) {
        setErrorMessage(error?.message || 'Failed to create payment method.');
        return;
      }

      const totalAmount = numberOfMonths * MONTHLY_FEE;
      const currentSubscription = subscriptionData?.subscription;

      // Check current subscription plan to determine which API to call
      if (currentSubscription?.plan_name === 'FREE') {
        // User is upgrading from FREE to STANDARD
        const subscriptionPayment: SubscriptionPaymentPayload = {
          payment_method_id: paymentMethod.id,
          amount: totalAmount,
          transaction_type: 'CHARGE',
          event_type: 'UPGRADE',
          plan_name: 'STANDARD',
          number_months: numberOfMonths
        };

        await SubscriptionApi.createSubscriptionPayment(subscriptionPayment);
        updateSessionStorageSubscriptionStatus('active');
        setMessage('Subscription payment processed successfully.');
      } else if (currentSubscription?.plan_name === 'STANDARD') {
        // User is extending existing STANDARD subscription
        const subscriptionPayment: SubscriptionPaymentPayload = {
          payment_method_id: paymentMethod.id,
          amount: totalAmount,
          transaction_type: 'CHARGE',
          event_type: 'EXTEND_PAID_PERIOD',
          plan_name: 'STANDARD',
          number_months: numberOfMonths
        };

        await SubscriptionApi.extendSubscriptionPayment(subscriptionPayment);
        setMessage('Subscription extended successfully.');
      } else {
        throw new Error('Unable to determine subscription type.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    } finally {
      setProcessing(false);
    }
  };

  const handleCardChange = (event: StripeCardNumberElementChangeEvent) => {
    setCardBrand(event.brand || '');
    setIsComplete(event.complete);
  };

  if (message) {
    return (
      <>
        <HorizontalMenu />
        <div className="container-safe py-6">
          <Breadcrumbs items={breadcrumbs} />
          <Alert variant="success" className="mt-6 text-lg p-5">
            {message} You will receive a confirmation email soon. Thank you!
          </Alert>
          <div className="mb-[250px]"></div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <HorizontalMenu />
        <div className="container-safe py-6">
          <Breadcrumbs items={breadcrumbs} />
          {errorMessage && (
            <Alert variant="error" className="mt-6">
              {errorMessage}
            </Alert>
          )}
          <Spinner />
        </div>
      </>
    );
  }

  const currentSubscription = subscriptionData?.subscription;

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-bold text-foreground mt-6 mb-4">Make Payment</h2>

        <div className="mb-6">
          {currentSubscription?.status === 'ACTIVE' && currentSubscription?.plan_name === 'FREE' ? (
            <p className="text-base text-foreground">
              Paid subscription is <strong>{FORMATTED_MONTHLY_FEE}</strong> per month. Please select how many months you'd like to enjoy unlimited services.&nbsp;
              {accountEmail ? (
                <span>
                  We will send you a confirmation email to <strong>{accountEmail}</strong>.
                  If this is not correct, please click the <strong>Account</strong> button at the top to update your email address.
                </span>
              ) : (
                <span>
                  We do not have an email address on file. Please click the <strong>Account</strong> button at the top to provide or update your email address.
                </span>
              )}
            </p>
          ) : currentSubscription?.status === 'ACTIVE' && currentSubscription?.plan_name === 'STANDARD' ? (
            <p className="text-base text-foreground">
              Your current subscription is active until{' '}
              <strong>{currentSubscription.current_period_end ? formatTimestamp(currentSubscription.current_period_end) : 'N/A'}</strong>. 
              Extend it now to continue uninterrupted service. Your extension will be added to your existing active subscription.&nbsp;
              {accountEmail ? (
                <span>
                  We will send you a confirmation email to <strong>{accountEmail}</strong>.
                  If this is not correct, please click the <strong>Account</strong> button at the top to update your email address.
                </span>
              ) : (
                <span>
                  We do not have an email address on file. Please click the <strong>Account</strong> button at the top to provide or update your email address.
                </span>
              )}
            </p>
          ) : (
            <p className="text-base text-foreground">
              Start your subscription today to access all our premium features.&nbsp;
              {accountEmail ? (
                <span>
                  We will send you a confirmation email to <strong>{accountEmail}</strong>.
                  If this is not correct, please click the <strong>Account</strong> button at the top to update your email address.
                </span>
              ) : (
                <span>
                  We do not have an email address on file. Please click the <strong>Account</strong> button at the top to provide or update your email address.
                </span>
              )}
            </p>
          )}
        </div>

        <div className="mb-5">
          <label className="flex items-center gap-2 text-base text-foreground w-full">
            <input
              type="checkbox"
              checked={true}
              disabled={true}
              className="size-4 flex-shrink-0"
            />
            <span className="flex-1">
              Fixed Term Subscription of {FORMATTED_MONTHLY_FEE}/Month. No contract required. Just select the number of months below.
            </span>
          </label>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl">
          <div className="mb-8">
            <div className="grid grid-cols-[auto_auto] gap-x-6 gap-y-2 items-start w-fit">
              <Label htmlFor="numberOfMonths" className="text-base">
                Number of Months:
              </Label>
              <Label className="text-base">
                Amount:
              </Label>
              <Input
                id="numberOfMonths"
                type="number"
                value={numberOfMonths}
                onChange={(e) => setNumberOfMonths(Math.max(1, parseInt(e.target.value, 10)))}
                min="1"
                max="60"
                className="w-24"
              />
              <div className="text-xl font-bold text-primary">
                ${(numberOfMonths * MONTHLY_FEE).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2.5 mb-2.5">
              <Label className="text-base mb-0">Card Number</Label>
              <div className="flex gap-1.5">
                {Object.entries(cardIcons).map(([type, src]) => (
                  <img
                    key={type}
                    src={src}
                    alt={`${type} icon`}
                    className={`w-10 h-[25px] ${
                      isComplete && cardBrand !== ''
                        ? cardBrand === type
                          ? 'block'
                          : 'hidden'
                        : 'block'
                    }`}
                  />
                ))}
                {isComplete && cardBrand === '' && (
                  <img
                    src={RedCross}
                    alt="Invalid card"
                    className="w-10 h-[25px]"
                  />
                )}
              </div>
            </div>
            <div className="border-2 border-muted rounded-2xl px-4 py-3 bg-white">
              <CardNumberElement options={cardStyle} onChange={handleCardChange} />
            </div>
          </div>

          <div className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-base mb-2.5 block">Expiration Date</Label>
                <div className="border-2 border-muted rounded-2xl px-4 py-3 bg-white">
                  <CardExpiryElement options={cardStyle} />
                </div>
              </div>

              <div>
                <Label className="text-base mb-2.5 flex items-center gap-2">
                  CVC (Card Verification Code)
                  <img src={BackofCard} alt="CVC Back of Card" className="w-[30px] h-[18px]" />
                </Label>
                <div className="border-2 border-muted rounded-2xl px-4 py-3 bg-white">
                  <CardCvcElement options={cardStyle} />
                </div>
              </div>
            </div>
          </div>

          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          <Button
            type="submit"
            disabled={processing}
            size="medium"
            variant="default"
          >
            {processing ? 'Processing...' : 'Make Payment'}
          </Button>
        </form>

        <div className="mt-5 flex items-center justify-center gap-2">
          <span>Payment is secured and processed by</span>
          <PoweredByStripe width={100} height="auto" />
        </div>
        <div className="mb-[100px]"></div>
      </div>
    </>
  );
};

export default () => (
  <Elements stripe={stripePromise}>
    <MakePaymentPage />
  </Elements>
);